import React, { FormEvent } from 'react';
import { Input } from 'reactstrap';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { Button, InputFile, InputText, Loading, SelectCustom, TextArea } from '@/components';
import { FormCommunication } from '@/features/communication/types';
import { OptionProps } from '@/components/Select';
import { FaWhatsapp } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { IoNotifications } from 'react-icons/io5';
import { BRAZIL_STATES } from '@/utils/BRAZIL_STATES';
import { States } from '@/helpers/common/states';

interface StateProps {
  state: States;
  formData: FormData;
  formErrors: FormErrors;
  events: OptionProps[] | undefined;
  type: string;
  selectedChannels: string[];
  cities: OptionProps[] | undefined;
  currentCityOption: OptionProps | null;
  selectedCities: OptionProps[];
  imageFileName: string;
}

interface DispatchProps {
  onChangeFormInput: OnChangeFormInput;
  onSubmit: (e: FormEvent) => void;
  toggleChannel: (channel: string) => void;
  onTypeChange: (value: 'TEMPLATE' | 'CUSTOM') => void;
  getCities: (uf: string) => Promise<OptionProps[]>;
  onAddCity: () => void;
  onRemoveCity: (value?: string | number) => void;
  setCurrentCityOption: (opt: OptionProps | null) => void;
  onChangeImage: (file?: File) => void;
}

type Props = StateProps & DispatchProps;

export const CommunicationEditUI: React.FC<Props> = ({
  state,
  formData,
  formErrors,
  selectedChannels,
  onChangeFormInput,
  onSubmit,
  events,
  type,
  toggleChannel,
  onTypeChange,
  cities,
  getCities,
  setCurrentCityOption,
  selectedCities,
  onAddCity,
  onRemoveCity,
  imageFileName,
  onChangeImage,
}): JSX.Element => {
  const CHANNELS = [
    { id: 'whatsapp', label: 'WhatsApp', Icon: FaWhatsapp },
    { id: 'gmail', label: 'Gmail', Icon: MdEmail },
    { id: 'notification', label: 'Notificação', Icon: IoNotifications },
  ] as const;

  const country = formData[FormCommunication.country] as string;

  return (
    <>
      <Loading isVisible={state === States.loading} />

      <form className="d-flex justify-content-center" onSubmit={onSubmit}>
        <div
          className="d-flex"
          style={{
            width: '100%',
            maxWidth: 1240,
            gap: 28,
            alignItems: 'flex-start',
            padding: '12px 0',
            flexWrap: 'wrap',
          }}
        >
          <div
            className="bg-white rounded-4 shadow-sm"
            style={{
              flex: 1,
              minWidth: 720,
              padding: 28,
              marginLeft: 10,
              borderRadius: 15,
            }}
          >
            <h2 style={{ fontSize: 34, fontWeight: 800, marginBottom: 18, color: '#4d4f50' }}>
              Editar campanha
            </h2>

            <div>
              <div style={{ fontWeight: 800, color: '#4d4f50' }}>Canal</div>

              <div className="d-flex gap-3 flex-wrap">
                {CHANNELS.map(({ id, label, Icon }) => {
                  const checked = selectedChannels.includes(id);

                  return (
                    <div
                      key={id}
                      onClick={() => toggleChannel(id)}
                      className="d-flex align-items-center gap-3"
                      style={{
                        padding: '12px 18px',
                        borderRadius: 10,
                        cursor: 'pointer',
                        userSelect: 'none',
                        background: '#fff',
                        minWidth: 200,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        readOnly
                        style={{
                          width: 16,
                          height: 16,
                          margin: 0,
                        }}
                      />
                      <Icon size={20} color="#111" style={{ flexShrink: 0, marginLeft: 3 }} />
                      <span style={{ fontWeight: 600 }}>{label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 700, marginTop: 20, marginBottom: 10, color: '#4d4f50' }}>
                Nome da Campanha
              </div>
              <InputText
                name="name"
                placeholder="Nome da campanha"
                value={formData[FormCommunication.name]}
                onChange={e => onChangeFormInput(FormCommunication.name)(e.target.value)}
                error={formErrors.name && formErrors.name[0]}
              />
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 10, color: '#4d4f50' }}>Estado</div>

              <SelectCustom
                name="country"
                placeholder="SELECIONE"
                value={formData[FormCommunication.country]}
                onChange={e => {
                  onChangeFormInput(FormCommunication.country)(e?.value as string);
                  getCities(e?.value as string);
                  onChangeFormInput(FormCommunication.city)('');
                }}
                error={formErrors.country && formErrors.country[0]}
                options={BRAZIL_STATES}
              />
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 10, color: '#4d4f50' }}>Cidade</div>

              <div className="d-flex gap-3" style={{ alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <SelectCustom
                    name="city"
                    placeholder="SELECIONE"
                    value={formData[FormCommunication.city]}
                    onChange={opt => {
                      setCurrentCityOption(opt ?? null);
                      onChangeFormInput(FormCommunication.city)(opt?.value as string);
                    }}
                    error={formErrors.city && formErrors.city[0]}
                    options={cities ?? []}
                    disabled={!country || !cities?.length}
                  />
                </div>

                <div style={{ width: 150, marginTop: 3, marginLeft: 7 }}>
                  <Button
                    title="Adicionar"
                    theme="outlineDark"
                    style={{ width: '100%', height: 44 }}
                    onClick={e => {
                      e.preventDefault();
                      onAddCity();
                    }}
                  />
                </div>
              </div>
            </div>

            {selectedCities.length > 0 && (
              <div>
                <div style={{ fontSize: 13, marginBottom: 8, color: '#4d4f50' }}>
                  Cidades adicionadas
                </div>

                <div className="d-flex flex-wrap gap-2">
                  {selectedCities.map(city => (
                    <div
                      key={String(city.value)}
                      className="d-flex align-items-center gap-2"
                      style={{
                        border: '1px solid #e5e7eb',
                        borderRadius: 10,
                        padding: '6px 10px',
                        fontWeight: 600,
                      }}
                    >
                      <span>{city.label}</span>
                      <button
                        type="button"
                        onClick={() => onRemoveCity(city.value)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          fontWeight: 900,
                          lineHeight: 1,
                        }}
                        aria-label={`Remover ${city.label}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div style={{ fontWeight: 700, marginBottom: 10, color: '#4d4f50' }}>
                Raio de alcance
              </div>

              <div className="d-flex gap-3 align-items-center">
                <div style={{ width: 180 }}>
                  <InputText
                    name="radius"
                    placeholder="0"
                    maxLength={3}
                    value={formData[FormCommunication.radius]}
                    onChange={e => onChangeFormInput(FormCommunication.radius)(e.target.value)}
                    error={formErrors.radius && formErrors.radius[0]}
                  />
                </div>

                <div style={{ color: '#4d4f50', fontWeight: 600, marginLeft: 5 }}>
                  Informe área de alcance.
                </div>
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 10, color: '#4d4f50' }}>
                Quantidade de mensagens
              </div>

              <InputText
                name="messageAmount"
                placeholder="0"
                maxLength={6}
                value={formData[FormCommunication.messageAmount]}
                onChange={e => onChangeFormInput(FormCommunication.messageAmount)(e.target.value)}
                error={formErrors.messageAmount && formErrors.messageAmount[0]}
              />
            </div>
            <div
              className="d-flex flex-wrap"
              style={{ columnGap: 28, rowGap: 18, marginBottom: 18 }}
            >
              <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                <div style={{ fontWeight: 700, marginBottom: 10, color: '#4d4f50' }}>
                  Data e hora de início
                </div>
                <InputText
                  name="startDate"
                  type="datetime-local"
                  value={formData[FormCommunication.startDate]}
                  onChange={e => onChangeFormInput(FormCommunication.startDate)(e.target.value)}
                  error={formErrors.startDate?.[0]}
                />
              </div>
              <div style={{ flex: '1 1 280px', minWidth: 0 }}>
                <div style={{ fontWeight: 700, marginBottom: 10, color: '#4d4f50' }}>
                  Data e hora de término
                </div>
                <InputText
                  name="endDate"
                  type="datetime-local"
                  value={formData[FormCommunication.endDate]}
                  onChange={e => onChangeFormInput(FormCommunication.endDate)(e.target.value)}
                  error={formErrors.endDate?.[0]}
                />
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 10, color: '#4d4f50' }}>Evento</div>

              <SelectCustom
                name="event"
                placeholder="SELECIONE"
                value={formData[FormCommunication.events] as any}
                onChange={e => onChangeFormInput(FormCommunication.events)(e?.value as string)}
                error={formErrors.events && formErrors.events[0]}
                options={events}
              />
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 10, color: '#4d4f50' }}>
                Tipo de mensagem
              </div>

              <div className="d-flex gap-3 ml-4 mb-3">
                <label
                  className="d-flex align-items-center gap-2"
                  style={{ cursor: 'pointer', marginRight: 30 }}
                >
                  <Input
                    type="radio"
                    name={FormCommunication.type_communication}
                    checked={type === 'TEMPLATE'}
                    onChange={() => onTypeChange('TEMPLATE')}
                  />
                  <span style={{ fontWeight: 600 }}>Template</span>
                </label>

                <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer' }}>
                  <Input
                    type="radio"
                    name={FormCommunication.type_communication}
                    checked={type === 'CUSTOM'}
                    onChange={() => onTypeChange('CUSTOM')}
                  />
                  <span style={{ fontWeight: 600 }}>Mensagem Personalizada</span>
                </label>
              </div>
            </div>

            <div>
              <div style={{ fontWeight: 700, marginBottom: 10, color: '#4d4f50' }}>
                {type === 'TEMPLATE' ? 'Nome do Template' : 'Mensagem'}
              </div>

              {type === 'TEMPLATE' ? (
                <>
                  <TextArea
                    name="template"
                    placeholder="Informe o nome do template"
                    value={formData[FormCommunication.template]}
                    onChange={e => onChangeFormInput(FormCommunication.template)(e.target.value)}
                    error={formErrors.template?.[0]}
                  />
                  <InputFile
                    name="communicationImageEdit"
                    label="Imagem do cabeçalho — obrigatória para templates com imagem (recomendado 1200x628)"
                    accept="image/jpeg,image/png"
                    fileName={imageFileName}
                    onChange={e => onChangeImage(e.target.files?.[0])}
                  />
                </>
              ) : (
                <TextArea
                  name="message"
                  placeholder="Digite sua mensagem..."
                  value={formData[FormCommunication.messages]}
                  onChange={e => onChangeFormInput(FormCommunication.messages)(e.target.value)}
                  error={formErrors.messages?.[0]}
                />
              )}

              <div style={{ color: '#9ca3af', fontSize: 12 }}>
                * Use as variáveis abaixo para indicar na mensagem informações do cliente ou event:
                <ul style={{ marginTop: 6, paddingLeft: 20 }}>
                  <li>@name - Nome do cliente.</li>
                  <li>@event - Nome do evento.</li>
                  <li>@date - data do evento.</li>
                  <li>@link - Acesso a pagina de venda do ingresso.</li>
                </ul>
              </div>
            </div>
          </div>

          <div
            className="bg-white rounded-4 shadow-sm"
            style={{
              width: 420,
              padding: 24,
              borderRadius: 15,
            }}
          >
            <div className="text-center" style={{ fontWeight: 800, fontSize: 22 }}>
              Resumo da campanha
            </div>

            <div
              style={{
                width: 56,
                height: 3,
                background: '#111',
                margin: '10px auto 18px',
                borderRadius: 99,
              }}
            />

            <div style={{ display: 'grid', rowGap: 10 }}>
              <div className="d-flex gap-2">
                <span style={{ fontWeight: 800, marginRight: 5 }}>Estado: </span>
                <span style={{ color: '#4d4f50', fontWeight: 600 }}>
                  {(formData[FormCommunication.country] as string) || '-'}
                </span>
              </div>

              <div className="d-flex gap-2">
                <span style={{ fontWeight: 800, marginRight: 5 }}>Cidade: </span>
                <span style={{ color: '#4d4f50', fontWeight: 600 }}>
                  {selectedCities?.length
                    ? selectedCities.map(c => c.label).join(', ')
                    : (formData[FormCommunication.city] as string) || '-'}
                </span>
              </div>

              <div className="d-flex gap-2">
                <span style={{ fontWeight: 800, marginRight: 5 }}>Raio de alcance:</span>
                <span style={{ color: '#4d4f50', fontWeight: 600 }}>
                  {(formData[FormCommunication.radius] as string) || '-'}
                </span>
              </div>

              <div className="d-flex gap-2">
                <span style={{ fontWeight: 800, marginRight: 5 }}>Evento:</span>
                <span style={{ color: '#4d4f50', fontWeight: 600 }}>
                  {(formData[FormCommunication.events] as string) || '-'}
                </span>
              </div>

              <div className="d-flex gap-2">
                <span style={{ fontWeight: 800, marginRight: 5 }}>Tipo:</span>
                <span style={{ color: '#4d4f50', fontWeight: 600 }}>
                  {type === 'TEMPLATE' ? 'Template' : 'Mensagem Personalizada'}
                </span>
              </div>

              <div className="d-flex gap-2">
                <span style={{ fontWeight: 800, marginRight: 5 }}>Quantidade de Mensagens:</span>
                <span style={{ color: '#4d4f50', fontWeight: 600 }}>
                  {(formData[FormCommunication.messageAmount] as string) || '-'}
                </span>
              </div>
              <div className="d-flex gap-2">
                <span style={{ fontWeight: 800, marginRight: 5 }}>Início:</span>
                <span style={{ color: '#4d4f50', fontWeight: 600 }}>
                  {(formData[FormCommunication.startDate] as string) || '-'}
                </span>
              </div>
              <div className="d-flex gap-2">
                <span style={{ fontWeight: 800, marginRight: 5 }}>Término:</span>
                <span style={{ color: '#4d4f50', fontWeight: 600 }}>
                  {(formData[FormCommunication.endDate] as string) || '-'}
                </span>
              </div>
            </div>

            <div className="d-flex justify-content-center" style={{ marginTop: 22 }}>
              <button
                type="submit"
                style={{
                  width: '100%',
                  height: 44,
                  borderRadius: 10,
                  border: 'none',
                  background: '#5b7ce2',
                  color: '#fff',
                  fontWeight: 800,
                  boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
                }}
              >
                Salvar alterações
              </button>
            </div>
          </div>
        </div>
      </form>
    </>
  );
};
